import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2, printHtml } from "../../lib/format";
import { monthLabel, payslipHtml } from "../../lib/hrDocs";
import { Notice, Stat } from "../../lib/ui";

/*
 * My payslips & leave - for every signed-in user (self-service). Shows the employee record linked to the login
 * (users.personal_number), completed payslips (view / print only), leave history and a leave request button.
 */

const tone = (s) => ({ approved: "ok", pending: "warn", rejected: "err" }[String(s || "").toLowerCase()] || "");

export default function MyHR() {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [slips, setSlips] = useState([]);
  const [leave, setLeave] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try {
      const m = await api.get("/api/me/staff"); setMe(m);
      if (m.linked) {
        const [p, l] = await Promise.all([api.get("/api/hr/payslips"), api.get("/api/hr/leave", { sort: "lr.start_date", order: "DESC" })]);
        setSlips(p.rows); setLeave(l);
      }
    } catch (e) { setError(e.message); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const taken = (leave?.rows || []).filter((r) => String(r.status).toLowerCase() === "approved").reduce((s, r) => s + Number(r.days || 0), 0);
  return (
    <main className="pg" style={{ maxWidth: "70rem" }}>
      <PageHeader slug="my_hr" actions={me?.linked && <Link to="/LeaveRequest" className="btn primary"><i className="fas fa-plus" /> Request leave</Link>} />
      <Notice notice={error && { tone: "err", text: error }} />
      {me && !me.linked && <div className="panel empty"><i className="fas fa-id-card" />Your login isn't linked to an employee record yet, so there are no payslips or leave to show. Ask an admin.</div>}
      {me?.linked && (<>
        <div className="ui-stats">
          <Stat icon="fa-id-badge" label={me.position || "Employee"} value={<span style={{ fontSize: "1rem" }}>{me.client_name}</span>} />
          <Stat icon="fa-file-invoice" tone="muted" label="Payslips" value={slips.length} />
          <Stat icon="fa-calendar-check" tone="up" label="Leave days approved" value={taken} />
          <Stat icon="fa-hourglass-half" tone="warn" label="Leave requests pending" value={(leave?.rows || []).filter((r) => String(r.status).toLowerCase() === "pending").length} />
        </div>
        <div className="grid2">
          <section className="panel card-pad"><h3>My payslips</h3>
            {slips.length === 0 ? <p style={{ color: "var(--muted)", margin: 0 }}>No payslips yet.</p> : (
              <table className="tbl"><thead><tr><th>Month</th><th className="num">Net pay</th><th /></tr></thead>
                <tbody>{slips.map((s) => <tr key={s.payroll_id}><td>{monthLabel(s.payment_month)}</td><td className="num mono" style={{ fontWeight: 700 }}>{n2(s.net_salary)}</td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right" }}><Link className="btn sm" to={`/payslip/${s.payroll_id}`}>View</Link>{" "}
                    <button type="button" className="btn sm" aria-label="Print" onClick={() => printHtml(payslipHtml(s))}><i className="fas fa-print" /></button></td></tr>)}</tbody></table>)}
          </section>
          <section className="panel card-pad"><h3>My leave</h3>
            {(leave?.rows || []).length === 0 ? <p style={{ color: "var(--muted)", margin: 0 }}>No leave requests yet.</p> : (
              <table className="tbl"><thead><tr><th>Type</th><th>Dates</th><th className="num">Days</th><th>Status</th></tr></thead>
                <tbody>{leave.rows.map((r) => <tr key={r.id}><td>{r.leave_type}<div style={{ fontSize: ".72rem", color: "var(--muted)" }}>{r.leave_class}</div></td>
                  <td style={{ whiteSpace: "nowrap" }}>{String(r.start_date).slice(0, 10)} – {String(r.end_date).slice(0, 10)}</td><td className="num mono">{r.days}</td>
                  <td><span className={`pill-tag ${tone(r.status)}`}>{r.status}</span></td></tr>)}</tbody></table>)}
          </section>
        </div>
      </>)}
    </main>
  );
}
